/**
 * NovaIcon — tiny sidebar-sized Nova avatar.
 *
 * #437 — rewired to render the canonical 2026 NovaExpression instead
 * of the old TV-head SVG stylization. Same external API (`className`,
 * `showAntenna`, `thinking`) so every callsite keeps working. Note
 * the character's palette is now fixed by design (ivory body, blue
 * eyes, orange saturn dot) — `currentColor` no longer paints it. The
 * sidebar's active state still reads because the row itself changes
 * background + text color; the icon is the constant.
 *
 * `thinking` maps to the "thinking" expression (antenna spin, orange
 * think-block eyes). `showAntenna=false` is honored via a small CSS
 * mask on the wrapper so callers that specifically hid the antenna
 * (rare) still get a clean head-only silhouette.
 */

import { cn } from "@/lib/utils";
import { NovaExpression } from "@/components/nova/kit";

export interface NovaIconProps {
  /** Tailwind sizing class — uses w/h. Default: w-5 h-5. */
  className?: string;
  /** Show the tiny antenna with the orange Saturn dot. Default true. */
  showAntenna?: boolean;
  /** Inverted-eye "thinking" variant. Default false. */
  thinking?: boolean;
}

export function NovaIcon({
  className,
  showAntenna = true,
  thinking = false,
}: NovaIconProps) {
  // NovaExpression is width-driven; sidebar callsites size via
  // Tailwind's w-5/h-5 on the wrapper. Render at 24px inside a
  // constrained box so the intrinsic svg scales down cleanly.
  return (
    <span
      aria-hidden="true"
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        // Small mask that trims the antenna if the caller opted out.
        !showAntenna && "[&_svg]:[clip-path:inset(20%_0_0_0)]",
        "w-5 h-5",
        className,
      )}
    >
      <NovaExpression
        id={thinking ? "thinking" : "idle"}
        size={26}
        pop={false}
        className="block"
      />
    </span>
  );
}

export default NovaIcon;
